import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import Articles from '../../articlesData';
import ArticleWrapper from '../../components/ArticleWrapper';
import { AudioPlayer } from '../../components/AudioProvider';
import { ExploreMoreLinks, ArticleTitleSection } from '../../components/ArticleComponents';


const WingTree = () => {
  const navigate = useNavigate();
  const article = Articles.find((a) => a.id === 'wingtree');
  useEffect(() => {
    if (!article.visible) {
      navigate('/in-progress');
    }
  }, [navigate, article.visible]);

  return (
    <ArticleWrapper articleId="wingtree">
      <div className="min-h-screen bg-[#111112] flex flex-col w-full">
        <ArticleTitleSection
          image='/pictures/articles/wingtree/banner.JPG'
          title='WingTree Review | A New Challenger in the Switch Market' 
          date={article.date}
          status={article.status}
        />
        {/* Content section with side-by-side layout */}

        
          <div className="w-full flex flex-col gap-16 px-4 bp1080:px-8 mb-16">

            <div className="w-full flex flex-col bp1080:flex-row-reverse items-center gap-8">
              <img
                src="/pictures/articles/wingtree/pic1.JPG"
                alt="WingTree Switches"
                className="w-full bp1080:w-[75vw] h-[60vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center bp1080:text-left bp1080:ml-[1vw] bp1080:mr-[3vw] bp1080:max-w-[54vw]">
                <h2 className="text-3xl font-bold mb-4">Disclaimer</h2>
                <p>
                  These switches were sent over by Unikeys for a review and sound test. Unikeys had no oversight or input in this review, and all thoughts written are my own. The links provided ARE affiliate links. Consider using them or code: <b>aeykbds</b> at checkout for a 7% discount.
                </p><br />
                <h2 className="text-3xl font-bold mb-4">What Is WingTree?</h2>
                   <p>
                      The mechanical keyboard switch market has never been more competitive. Between established giants like <b>Cherry</b> and modern innovators such as <b>HMX</b> and <b>Keygeek</b>, there’s no shortage of options for enthusiasts. Enter <b>WingTree</b>, a brand-new manufacturer aiming to strike a balance between classic inspirations and modern innovation.
                    </p>
                    <br />
                    <p>
                      With their debut lineup, WingTree has released two series of switches: the <b>Yuying</b> and <b>Yunie</b>, each with both a linear and tactile switch. They’ve also introduced a Hall Effect (HE) switch, but I'll only be focusing on the four mechanical offerings.
                    </p>
                <br />
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row items-center gap-8">
              <img
                src="/pictures/articles/wingtree/pic2.jpg"
                alt="Yunie Linear and Tactile Scatter"
                className="w-full md:w-[45vw] h-[80vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:ml-[2vw] md:text-left md:max-w-[45vw]">
                <h2 className="text-3xl font-bold mb-4">WingTree Yuying Series: A Modern Take on the Classics</h2>
                  <p>
                    The <b>Yuying series</b> is WingTree’s homage to the classic <b>Cherry MX Blacks</b> and <b>Cherry MX Browns</b>. These switches intentionally replicate the feel, sound, and even the design of Cherry’s most recognizable switches, but with tweaks that aim to make them more usable out of the box.
                  </p>
                  <h3 className="text-2xl font-bold mt-6 mb-2">Yuying Linear</h3>
                  
                  <ul className="list-disc mt-3 ml-6 mb-3">
                    <li><b>Sound Profile:</b> A bright, high-pitched bottom-out with an even sharper, sometimes shrill, top-out. While the top-out sound may divide opinions, it gives the switch a distinct character.</li>
                    <li><b>Feel:</b> Slightly scratchy, but smoother than stock Cherry switches. The included 19.5mm spring provides a very snappy and comfortable typing experience.</li>
                    <li><b>Thoughts:</b> I’d say this switch is about 70% of the way to a fully modded MX2A Black. It’s brighter, longer-poled, and easier to use without modification.</li>
                  </ul>
                  <p>
                    At just{' '}
                    <a
                      href="https://unikeyboards.com/products/wingtree-yuying-linear-switch-factory-lubed-10pcs?sca_ref=9159805.HnviW9XZM5"
                      target="_blank"
                      rel="noopener noreferrer"
                      className=" font-bold underline text-blue-400 hover:text-blue-300"
                    >
                      $3.50
                  </a>
                  {' '}per 10-pack, it’s a compelling option for anyone who likes the Cherry sound-signature but doesn’t want to deal with lubing and filming.
                  </p>
                  <AudioPlayer src="/audio/wingtree/neo65cu_yuying_linear.mp3" className="w-full mt-5" />
                  <p className='text-[1rem] text-center mt-[0.5vh]'>Neo65 Cu | Yuying Linear | Carbon Fiber Plate | Gasket Mount | No Foams</p>
                  <AudioPlayer src="/audio/envoy/envoy_yuying_linear.mp3" className="w-full mt-5" />
                  <p className='text-[1rem] text-center mt-[0.5vh]'>Mode Envoy | Yuying Linear | Aluminum Plate | Green Lattice Mount | No Foams</p>
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row-reverse items-center gap-8">
              <img
                src="/pictures/articles/wingtree/pic3.JPG"
                alt="Yuying Tactile Showcase"
                className="w-full md:w-[45vw] h-[70vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:text-left">
                <h3 className="text-2xl font-bold mt-6 mb-2">Yuying Tactile</h3>
                <ul className="list-disc ml-6 mb-2">
                  <li><b>Sound Profile:</b> Bright but full, well-rounded, and free of the shrill top-out present in the linears. Has a slightly scratchy sound reminiscent of Cherry MX Browns.</li>
                  <li><b>Feel:</b> A light tactile bump that’s small yet noticeable. The 19mm spring results in a snappy feel that is well-suited for long typing sessions.</li>
                  <li><b>Thoughts:</b> A solid light tactile that’s not too heavy, perfect for those who enjoy a responsive typing experience without being overwhelmed by a heavy switch.</li>
                </ul>
                <p>
                  Priced at {' '}
                  <a
                    href="https://unikeyboards.com/products/wingtree-yuying-light-tactile-switch-factory-lubed-10pcs?sca_ref=9159805.HnviW9XZM5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className=" font-bold underline text-blue-400 hover:text-blue-300"
                  >
                    $3.50
                  </a>
                  {' '}per 10-pack, it’s a great option for anyone who enjoys a bright sounding light tactile switch without the need for extensive modifications.
                </p>
                <AudioPlayer src="/audio/wingtree/neo65cu_yuying_tactile.mp3" className="w-full mt-5" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Neo65 Cu | Yuying Tactile | Carbon Fiber Plate | Gasket Mount | No Foams</p>
                <h3 className="text-2xl font-bold mt-6 mb-2">Disclaimer</h3>
                <p>
                  My prototype units exhibited some <b>chattering issues</b>, with only 61 of 90 switches working properly. Unikeys has since confirmed that production units will feature <b>improved factory lubing and leaf design</b>. I’ll be revisiting these once I test production models.
                </p>
                
               
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row items-center gap-8">
              <img
                src="/pictures/articles/wingtree/pic4.JPG"
                alt="Yunie Linear and Tactile Scatter"
                className="w-full md:w-[45vw] h-[130vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:text-left md:ml-[2vw] md:max-w-[45vw]">
                <h2 className="text-3xl font-bold mb-4">WingTree Yunie Series: Smooth and Modern</h2>
                  <p>
                    While the Yuying series looks to the past, the <b>Yunie lineup</b> embraces modern design, factory lubing, and long-pole stem configurations reminiscent of HMX or Keygeek switches.
                  </p>
                  <h3 className="text-2xl font-bold mt-6 mb-2">Yunie Linear</h3>
                  <ul className="list-disc ml-6 mb-2">
                    <li><b>Sound Profile:</b> Mid-pitched and mellow, with a subtle bottom-out despite the long-pole stem.</li>
                    <li><b>Feel:</b> Smooth thanks to factory lubing, though some inconsistencies in application mean you might feel slight variation between switches.</li>
                    <li><b>Thoughts:</b> Nothing groundbreaking, but a solid choice for those seeking a reliable linear switch.</li>
                  </ul>
                  <p>
                    While the switch itself is fairly average, it’s impressive that a newcomer’s first release can already compete with established names like HMX and Keygeek. At{' '}
                  <a
                    href="https://unikeyboards.com/products/wingtree-yunie-linear-switch-factory-lubed-10pcs?sca_ref=9159805.HnviW9XZM5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className=" font-bold underline text-blue-400 hover:text-blue-300"
                  >
                    $3.50
                  </a>                    
                    {' '}per 10-pack, it’s priced competitively and provides a reliable, mellow switch for daily use.
                  </p>
                  <AudioPlayer src="/audio/wingtree/neo65cu_yunie_linear.mp3" className="w-full mt-5" />
                  <p className='text-[1rem] text-center mt-[0.5vh]'>Neo65 Cu | Yunie Linear | Carbon Fiber Plate | Gasket Mount | No Foams</p>
                  <AudioPlayer src="/audio/krush/krush_yunie_linear.mp3" className="w-full mt-5" />
                  <p className='text-[1rem] text-center mt-[0.5vh]'>Krush65 | Yunie Linear | Aluminum Plate | Oring Mount | No Foams</p>
                  <h3 className="text-2xl font-bold mt-6 mb-2">Yunie Tactile</h3>
                  <ul className="list-disc ml-6 mb-2">
                    <li><b>Sound Profile:</b> Deep, clean, and well-suited for foamy or thocky builds.</li>
                    <li><b>Feel:</b> A 0 pretravel heavy tactile bump that dominates the keystroke, making for an engaging and unique typing experience. The bump is rounded and pleasant, with only a very short linear travel after.</li>
                    <li><b>Thoughts:</b> Personally, I found the 3.2mm total travel distance a bit short and jarring, but tactile lovers may find it perfect.</li>
                  </ul>
                  <p>
                    At{' '}
                  <a
                    href="https://unikeyboards.com/products/wingtree-yunie-tactile-switch-factory-lubed-10pcs?sca_ref=9159805.HnviW9XZM5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className=" font-bold underline text-blue-400 hover:text-blue-300"
                  >
                    $3.50
                  </a>
                    {' '} per 10-pack, it also undercuts its heavy tactile competitors, namely the Gateron Type-R and Haimu Mulberry. For budget-conscious tactile fans, this is a very attractive option.
                  </p>
                  <AudioPlayer src="/audio/wingtree/neo65cu_yunie_tactile.mp3" className="w-full mt-5" />
                  <p className='text-[1rem] text-center mt-[0.5vh]'>Neo65 Cu | Yunie Tactile | Carbon Fiber Plate | Gasket Mount | No Foams</p>
                  <AudioPlayer src="/audio/neoergo/neoergo_yunie_tactile_pcfoam.mp3" className="w-full mt-5" />
                  <p className='text-[1rem] text-center mt-[0.5vh]'>Neo Ergo | Yunie Tactile | Polypropylene Plate | Gasket Mount | Plate and Case Foams</p>
                  
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row-reverse items-center gap-8">
              <img
                src="/pictures/articles/wingtree/pic5.JPG"
                alt="Yunie Linear Showcase"
                className="w-full md:w-[55vw] h-[80vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:mr-[2vw] md:ml-[1vw] md:text-left md:max-w-[45vw]">
                <h2 className="text-3xl font-bold mb-4">Final Thoughts: A Promising Start for WingTree</h2>
                <p>
                  WingTree’s debut is <b>ambitious</b>. They're taking on Cherry’s classics while offering modern alternatives in the same lineup. And in many ways, they’ve <b>succeeded</b>.
                </p>
                <br />
                <p>
                  The <b>Yuying linears</b> are a brighter, easier-to-use Cherry Black, while the <b>Yuying tactiles</b> are arguably the most impressive Cherry Brown alternative I’ve tried(pending production QC improvements).
                </p>
                <br />
                <p>
                  The <b>Yunie linears</b> are solid, if unremarkable, but provide a reliable option at a fair price.
                </p>
                <br />
                <p>
                  The <b>Yunie tactiles</b> are WingTree’s breakout hit: affordable, unique, and genuinely competitive with switches like the Gateron Type-R and Haimu Mulberry.
                </p>
                <br />
                <p>
                  At just{' '}
                  <a
                    href="https://unikeyboards.com/collections/wingtree?sca_ref=9159805.HnviW9XZM5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className=" font-bold underline text-blue-400 hover:text-blue-300"
                  >
                    $3.50
                  </a>
                  {' '}per 10-pack, WingTree is positioning itself as both a <b>budget-friendly</b> and <b>enthusiast-friendly</b> option. Minor issues like inconsistent lubing or sharp top-out sounds are forgivable for a <b>first release</b>. If WingTree can tighten quality control, they could very well join HMX and Keygeek as one of the new mainstream switch manufacturers.
                </p>
                <h3 className="text-2xl font-bold mt-6 mb-2">Verdict:</h3>
                <p>
                  Four very promising switches from a brand-new company. <b>Keep an eye on WingTree.</b>
                </p>
              </div>
            </div>


          </div>
          {/* Video section */}
        <div className="w-full flex flex-col items-center justify-center mb-24">
          <h2 className="text-3xl font-bold text-white mb-4 text-center">In Depth Review</h2>
          <div className="w-full flex justify-center">
            <iframe
              width="900"
              height="506"
              src="https://www.youtube.com/embed/LdRvCJdKSuM?si=JVTddMvz9dCYINET"
              title="Pavlov Build and Sound Test"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-lg shadow-lg mx-auto w-full max-w-4xl"
            ></iframe>
          </div>
        </div>
        {/* Links section */}
        <div className="w-full flex flex-col items-center mb-24">
          <ExploreMoreLinks/>
        </div>
      </div>
    </ArticleWrapper>
  );
};

export default WingTree;


